import { forwardRef, type ComponentType } from 'react';
import { View } from '@amzn/stencil-react-components/layout';
import { IconInformationSmall } from '@amzn/stencil-react-icons';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';

interface InfoIconProps {
  readonly ariaLabel: string;
}

/**
 * Bare info icon, forwardRef so Stencil's `withTooltip` can attach its trigger
 * ref. `aria-label` makes the trigger discoverable to assistive tech (the
 * tooltip text itself is announced on focus/hover by the Stencil wrapper).
 */
const InfoIcon = forwardRef<HTMLDivElement, InfoIconProps>(function InfoIcon(
  { ariaLabel },
  ref,
) {
  return (
    <View ref={ref} display="inline-flex" role="img" aria-label={ariaLabel}>
      <IconInformationSmall aria-hidden="true" />
    </View>
  );
});

// withTooltip's return type resolves through @amzn/stencil-react-desc, which
// the strict lint/type config sees as an unresolved (error) type — so the
// HOC application isn't callable/assignable without help. The HOC contract is
// stable, so treat it as a factory that wraps a component and adds the tooltip
// props we pass. This localizes the single unavoidable cast to one line.
type TooltipHoc = () => (
  c: ComponentType<InfoIconProps>,
) => ComponentType<InfoIconProps & TooltipExtraProps>;

interface TooltipExtraProps {
  readonly tooltipText: string;
  readonly isInline?: boolean;
  readonly tooltipDataTestId?: string;
}

const InfoIconWithTooltip = (withTooltip as unknown as TooltipHoc)()(InfoIcon);

export interface InfoTooltipProps {
  /** The help text shown in the tooltip. */
  readonly text: string;
  /** Accessible label for the icon trigger (e.g. "More information"). */
  readonly ariaLabel: string;
  readonly dataTestId?: string;
}

/**
 * A small "ⓘ" info icon that reveals `text` in a Stencil tooltip on
 * hover/focus. Site-wide affordance for field-level help next to a form
 * label — pairs with `RequiredLabel` in a label row. Reuses Stencil's
 * `withTooltip` so positioning, focus handling, and portal behavior stay
 * consistent with the rest of the design system.
 */
export function InfoTooltip({
  text,
  ariaLabel,
  dataTestId,
}: InfoTooltipProps): JSX.Element {
  return (
    <InfoIconWithTooltip
      ariaLabel={ariaLabel}
      tooltipText={text}
      isInline
      {...(dataTestId !== undefined ? { tooltipDataTestId: dataTestId } : {})}
    />
  );
}
